// ═══════════════════════════════════════════════════════════════════════════
// COLLAB-LOCK-PROPERTY (W3.5, MASTERPLAN H2) — read-only lock-status /
// lock-owner property surface (PURE, no store reads, no I/O)
// ═══════════════════════════════════════════════════════════════════════════
//
// Closes the `sub-collab·properties` MASTERPLAN hole: COLLAB-LOCK-COMMAND
// (#534) shipped the LOCK/UNLOCK CLI + the fail-closed `dispatch()` veto
// (`collabLockVeto.ts`), but never surfaced the resulting `LockState` in the
// Properties panel — a user selecting an entity another collaborator holds
// could not SEE why, only discover it after a rejected edit. This module is
// the pure read-through projection: given a selected entity's id/layer, the
// live soft-lock map, this tab's own participant id, and whether that map is
// currently AUTHORITATIVE, resolve the entity's EFFECTIVE lock and project it
// into the three read-only property rows MASTERPLAN §5.5 names: **Locked**
// (yes/no), **Locked by** (owner display name, or "You" for a self-held
// lock), **Lock acquired at** (the lock's `grantedAtMs`, locale-formatted).
//
// ── Precedence (mirrors `evaluateCollabLockVeto` EXACTLY, local-Codex P2
// finding round 1) ───────────────────────────────────────────────────────
// An entity-scope and a layer-scope lock are independent — `LockManager`
// keys them separately, so a participant can hold a SELF entity-scope lock
// on an entity whose LAYER is simultaneously locked by SOMEONE ELSE. The
// veto's `dispatch()` check walks entity-scope first but does NOT
// short-circuit on a self-held entity lock — it falls through to the layer
// check regardless, so a layer lock held by another participant is what
// actually blocks an edit here even when the entity's own lock is self-held.
// An earlier revision of this resolver returned the self-held entity lock
// unconditionally ("Locked by You"), which HID that real conflict from the
// panel. The corrected precedence, per target:
//   1. entity-scope lock held by ANOTHER participant — wins outright (most
//      specific possible blocker).
//   2. else layer-scope lock held by ANOTHER participant — this is what
//      will actually veto a dispatch on this entity.
//   3. else the entity-scope lock IF held by self (informational — you may
//      still edit; entity preferred over layer for specificity).
//   4. else the layer-scope lock IF held by self.
//   5. else unlocked.
//
// READ-THROUGH ONLY (CLAUDE.md ПРИОРИТЕТ #2.1) — this module defines NO new
// lock semantics and performs NO acquire/release; it only PROJECTS the typed
// `LockState` COLLAB-LOCK-COMMAND already established. Acquiring/releasing a
// lock stays exclusively the LOCK/UNLOCK CLI's job (`editorStore.ts`'s CLI
// intercept -> `useCollabLockStore.requestLocks/releaseLocks`); nothing here
// ever calls those.
//
// PURE by construction (mirrors `collabLockVeto.ts`): no `useCollabLockStore`
// read, no i18n singleton import, no DOM/React. Every external input
// (`locks`, `selfParticipantId`, `authoritative`, the `t`/`language`
// formatters) is a parameter — the caller (`InspectorSidebar.tsx`) reads the
// live stores via hooks (so the panel re-renders reactively on a lock change)
// and passes the current snapshot in. This is what makes the module
// exhaustively unit-testable without mounting React or opening a socket, and
// what guarantees the ZERO-OVERHEAD-WHEN-DISCONNECTED invariant carries over
// unchanged: `buildCollabLockPropertyRows` is only ever called by the caller
// when a collab session is not `"disconnected"` (see the call site) — an
// ordinary, non-collaborating user's Properties panel renders byte-identical
// to before this module existed.
//
// ── `authoritative` (mirrors `collabLockVeto.ts`'s P1) ──────────────────────
// `true` for a live `"connected"` session (the map is kept in sync by
// `welcome` + `lock-update`); `false` for a tab that WAS connected and is now
// `"connecting"` (mid-reconnect) or `"error"` — `CollabLockClient.
// resetTrackedState()` clears `locks` to EMPTY on those transitions even
// though the true server-side picture is simply UNKNOWN right now. An
// unauthoritative snapshot resolves to `{ kind: "unknown" }` for every
// target — never silently reported as "unlocked", the same fail-closed
// posture the veto takes for an edit.
// ═══════════════════════════════════════════════════════════════════════════

import { lockScopeKey, type LockScope, type LockState } from "../collab/collabLockTypes";
import type { LanguageCode, MessageKey } from "../i18n/messages";

/** The canonical English property-row labels this module owns (i18n keys are
 *  derived from these the SAME way every other row derives one —
 *  `PropertyGrid.translatePropertyLabel` / `propertyMessageKey` — so no
 *  separate key-mapping table is needed: "Locked" -> `property.locked`,
 *  "Locked by" -> `property.locked_by`, "Lock acquired at" ->
 *  `property.lock_acquired_at`). */
export const COLLAB_LOCK_PROPERTY_LABELS = {
  locked: "Locked",
  lockedBy: "Locked by",
  lockAcquiredAt: "Lock acquired at",
} as const;

/** The minimal projection of a selected entity this module needs — its DXF
 *  handle (== `LockScope.targetId` for an entity-scope lock, `DrawingEntity.id`
 *  at the call site) and its layer name (case-insensitively matched against a
 *  layer-scope lock, mirroring `collabLockVeto.ts`'s LAYER-scope handling). */
export type EntityLockPropertyTarget = {
  entityId: string;
  layerName: string | undefined;
};

/**
 * The resolved lock state for ONE target, ready for display:
 *   - `"unlocked"`   — no entity-scope AND no layer-scope lock currently held.
 *   - `"locked"`     — the effective lock per the precedence above, plus
 *                      whether THIS tab holds it.
 *   - `"unknown"`    — `authoritative` was `false`; the true state cannot be
 *                      proven right now (P1 fail-closed display, not a guess).
 */
export type EntityLockPropertySnapshot =
  | { kind: "unlocked" }
  | { kind: "locked"; scope: LockScope; lock: LockState; heldBySelf: boolean }
  | { kind: "unknown" };

/** A case-insensitive layer-name -> `LockState` index built from every
 *  layer-scope entry in `locks`, ONCE per projection (local-Codex P2 finding
 *  round 1 — mirrors `collabLockVeto.ts`'s own `layerLockIndex`: building
 *  this per-CALLER-loop-iteration instead of per-target turns an O(targets ×
 *  locks) scan into O(targets + locks), which matters at the documented
 *  soft-lock scale — up to 64 participants × 512 locks each). */
function buildLayerLockIndex(
  locks: ReadonlyMap<string, LockState>,
): Map<string, LockState> {
  const index = new Map<string, LockState>();
  for (const lock of locks.values()) {
    if (lock.scope.kind === "layer") {
      index.set(lock.scope.targetId.toLowerCase(), lock);
    }
  }
  return index;
}

function resolveOne(
  target: EntityLockPropertyTarget,
  locks: ReadonlyMap<string, LockState>,
  layerLockIndex: ReadonlyMap<string, LockState> | null,
  selfParticipantId: string | null,
): EntityLockPropertySnapshot {
  const entityScope: LockScope = { kind: "entity", targetId: target.entityId };
  const entityLock = locks.get(lockScopeKey(entityScope));

  // (1) entity-scope lock held by ANOTHER participant wins outright.
  if (entityLock !== undefined && entityLock.ownerId !== selfParticipantId) {
    return { kind: "locked", scope: entityScope, lock: entityLock, heldBySelf: false };
  }

  const layerLock =
    target.layerName !== undefined && layerLockIndex !== null
      ? layerLockIndex.get(target.layerName.toLowerCase())
      : undefined;

  // (2) a layer-scope lock held by ANOTHER participant is what actually
  // blocks an edit here — checked even when the entity's own lock is
  // self-held (or absent), matching the veto's fall-through.
  if (layerLock !== undefined && layerLock.ownerId !== selfParticipantId) {
    return { kind: "locked", scope: layerLock.scope, lock: layerLock, heldBySelf: false };
  }

  // (3)/(4) nothing external conflicts — report a self-held lock for
  // informational completeness (entity preferred for specificity).
  if (entityLock !== undefined) {
    return { kind: "locked", scope: entityScope, lock: entityLock, heldBySelf: true };
  }
  if (layerLock !== undefined) {
    return { kind: "locked", scope: layerLock.scope, lock: layerLock, heldBySelf: true };
  }

  return { kind: "unlocked" };
}

/**
 * Resolve the effective lock snapshot for EVERY target in a selection in a
 * SINGLE pass — O(targets + locks), not O(targets × locks) (local-Codex P2
 * finding round 1). This is the primary entry point; `InspectorSidebar.tsx`
 * calls this ONCE per render with the whole selection rather than mapping
 * a per-entity resolver (which would rebuild the layer index every entity).
 */
export function resolveEntityLockPropertySnapshots(
  targets: readonly EntityLockPropertyTarget[],
  locks: ReadonlyMap<string, LockState>,
  selfParticipantId: string | null,
  authoritative: boolean,
): EntityLockPropertySnapshot[] {
  if (!authoritative) {
    return targets.map((): EntityLockPropertySnapshot => ({ kind: "unknown" }));
  }
  const needsLayerIndex = targets.some((target) => target.layerName !== undefined);
  const layerLockIndex = needsLayerIndex ? buildLayerLockIndex(locks) : null;
  return targets.map((target) =>
    resolveOne(target, locks, layerLockIndex, selfParticipantId),
  );
}

/** Convenience single-target wrapper over {@link resolveEntityLockPropertySnapshots}
 *  (unit tests / any single-selection-only caller) — NOT used by
 *  `InspectorSidebar.tsx`'s multi-entity render path, which calls the plural
 *  form directly to share ONE layer-lock index across the whole selection. */
export function resolveEntityLockPropertySnapshot(
  target: EntityLockPropertyTarget,
  locks: ReadonlyMap<string, LockState>,
  selfParticipantId: string | null,
  authoritative: boolean,
): EntityLockPropertySnapshot {
  const [snapshot] = resolveEntityLockPropertySnapshots(
    [target],
    locks,
    selfParticipantId,
    authoritative,
  );
  // `targets` always has exactly 1 element here, so `.map` always produces
  // exactly 1 result — this is a totality proof, not a real fallback.
  return snapshot ?? { kind: "unlocked" };
}

/** The PropertyGrid row shape this module produces — always `readOnly: true`
 *  (S16-ARRAYRECT-READONLY-ALIGN pattern: a lock's owner/timestamp can never
 *  be edited from the panel, only acquired/released via LOCK/UNLOCK). */
export type CollabLockPropertyRow = { label: string; value: string; readOnly: true };

/** The `t`/`language` this module needs, injected by the caller (`useT()` /
 *  `useLanguage()` in `InspectorSidebar.tsx`) so the module itself never
 *  imports the i18n singleton (stays PURE + trivially unit-testable with a
 *  stub translator) while still reacting to a live language switch (the
 *  caller's hook-sourced `t` closure changes identity on every language
 *  change, which the caller re-runs this builder against). */
export type LockPropertyRowBuilderDeps = {
  t: (key: MessageKey, vars?: Record<string, string | number>) => string;
  language: LanguageCode;
};

const DASH = "—";

/**
 * Build the 3 read-only lock-property rows for a selection of 1..N entities.
 * Single-select is the common case (N=1, no aggregation branch taken).
 * Multi-select AGGREGATES across every entity's resolved snapshot exactly
 * like `panelPropertyRows.ts`'s `buildTypedMultiSelectPropertyValues` does
 * for every other shared property: a value common to the WHOLE selection is
 * shown as-is, a value that differs across the selection renders
 * `t("props.varies")` (never a per-entity mix silently picking one side).
 * Returns `[]` for an empty selection — the caller only invokes this for a
 * non-empty selection, but an empty array is the safe/no-op result either way.
 */
export function buildCollabLockPropertyRows(
  snapshots: readonly EntityLockPropertySnapshot[],
  deps: LockPropertyRowBuilderDeps,
): CollabLockPropertyRow[] {
  if (snapshots.length === 0) return [];
  const { t, language } = deps;
  return [
    {
      label: COLLAB_LOCK_PROPERTY_LABELS.locked,
      value: aggregateLockedValue(snapshots, t),
      readOnly: true,
    },
    {
      label: COLLAB_LOCK_PROPERTY_LABELS.lockedBy,
      value: aggregateLockedBy(snapshots, t),
      readOnly: true,
    },
    {
      label: COLLAB_LOCK_PROPERTY_LABELS.lockAcquiredAt,
      value: aggregateAcquiredAt(snapshots, t, language),
      readOnly: true,
    },
  ];
}

type LockedSnapshot = Extract<EntityLockPropertySnapshot, { kind: "locked" }>;

function isLocked(snapshot: EntityLockPropertySnapshot): snapshot is LockedSnapshot {
  return snapshot.kind === "locked";
}

function aggregateLockedValue(
  snapshots: readonly EntityLockPropertySnapshot[],
  t: LockPropertyRowBuilderDeps["t"],
): string {
  if (snapshots.some((s) => s.kind === "unknown")) {
    return t("collab_lock_property.unknown");
  }
  const allLocked = snapshots.every(isLocked);
  if (allLocked) return t("collab_lock_property.yes");
  const allUnlocked = snapshots.every((s) => s.kind === "unlocked");
  if (allUnlocked) return t("collab_lock_property.no");
  return t("props.varies");
}

/** Every entity in the selection is locked AND every one resolves to the
 *  SAME underlying lock (same owner participant / same grant timestamp) —
 *  the precondition both `aggregateLockedBy` and `aggregateAcquiredAt` share
 *  before reading a single common value off the (arbitrary) first entry. */
function commonLockedSnapshots(
  snapshots: readonly EntityLockPropertySnapshot[],
): readonly LockedSnapshot[] | null {
  if (snapshots.some((s) => s.kind === "unknown")) return null;
  const locked = snapshots.filter(isLocked);
  if (locked.length === 0 || locked.length !== snapshots.length) return null;
  return locked;
}

function aggregateLockedBy(
  snapshots: readonly EntityLockPropertySnapshot[],
  t: LockPropertyRowBuilderDeps["t"],
): string {
  if (snapshots.some((s) => s.kind === "unknown")) return DASH;
  const locked = commonLockedSnapshots(snapshots);
  if (locked === null) {
    // Either nobody is locked (dash) or a mixed locked/unlocked selection
    // (varies) — distinguish by whether ANY entity is locked at all.
    return snapshots.some(isLocked) ? t("props.varies") : DASH;
  }
  const ownerIds = new Set(locked.map((s) => s.lock.ownerId));
  if (ownerIds.size > 1) return t("props.varies");
  const first = locked[0];
  if (!first) return DASH;
  return first.heldBySelf ? t("collab_lock_property.you") : first.lock.ownerName;
}

function aggregateAcquiredAt(
  snapshots: readonly EntityLockPropertySnapshot[],
  t: LockPropertyRowBuilderDeps["t"],
  language: LanguageCode,
): string {
  if (snapshots.some((s) => s.kind === "unknown")) return DASH;
  const locked = commonLockedSnapshots(snapshots);
  if (locked === null) {
    return snapshots.some(isLocked) ? t("props.varies") : DASH;
  }
  const grantedTimes = new Set(locked.map((s) => s.lock.grantedAtMs));
  if (grantedTimes.size > 1) return t("props.varies");
  const first = locked[0];
  if (!first) return DASH;
  return new Date(first.lock.grantedAtMs).toLocaleString(
    language === "ru" ? "ru-RU" : "en-US",
  );
}
